import { cn } from "@shared/lib/utils";
import "@/components/renewal/fx/fx.css";
import { finishGlow } from "@/components/draw/finishGlow";
import { cardArtSrc } from "@/lib/cardArt";
import { CARD_FINISHES, type CardFinishMeta } from "@/lib/cards";

/** 대표 아트 에이전트 — openai-gpt만 base·gold_vinyl까지 실카드 아트 보유. */
const LADDER_AGENT = "openai-gpt";

/**
 * 피니시 대표 아트 5:7 프레임 — 실카드 아트가 있으면 그 아트, 없으면
 * finishMeta 강조색 자리표시(★). RarityLadder 칸과 ShopPage 팩 트랙
 * 라인업 프리뷰 타일이 공유한다.
 */
export function FinishArt({ meta, className }: { meta: CardFinishMeta; className?: string }) {
  const src = cardArtSrc(LADDER_AGENT, meta.finish);
  return (
    <span
      className={cn(
        "relative block aspect-[723/1024] overflow-hidden rounded-xl border border-line bg-(--ty-bg-2)",
        className,
      )}
    >
      {src ? (
        <>
          <img
            src={src}
            // 사다리 칸은 ≤128px — 1x(723px)로도 충분, @2x 전송량 절감
            alt={meta.label}
            loading="lazy"
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover"
          />
          {/* Holo = Base 아트 + 정적 포일 오버레이 (인터랙티브 홀로는 PioneerHoloCard 표면 담당) */}
          {meta.finish === "holo" && <span aria-hidden className="ty-foil absolute inset-0" />}
        </>
      ) : (
        /* 아트 공개 전 상태(improvements §S-5) — 등급색 방사형 명도 슬랩 +
           이중부호(● open / ◆ serial / ★ 1-of-1) + 발행량 /N — 블러·글로우 금지, 색 단차만 */
        <span aria-hidden className="absolute inset-0">
          <span
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse at 50% 38%, ${finishGlow(meta.finish)} 0%, transparent 72%)`,
            }}
          />
          <span className="absolute inset-0 grid place-items-center">
            <span className="flex flex-col items-center gap-1.5">
              <span className={cn("text-2xl leading-none", meta.accentText)}>
                {meta.supplyCap === 1 ? "★" : meta.supplyCap != null ? "◆" : "●"}
              </span>
              <span className="font-mono text-[10px] font-medium tabular-nums text-ink-dim">
                {meta.supplyCap != null ? `/${meta.supplyCap}` : "Open"}
              </span>
            </span>
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * 등급 티어 사다리(§2C) — 8피니시 가로 사다리, 낮→높. 칸 = [대표 아트 /
 * RarityBadge / 발행량 tnum]. 상위 티어일수록 칸이 위로 떠오르는 계단
 * 오프셋으로 등급 상승 방향이 공간으로 읽힌다. 가로 스크롤 + 24px edge mask
 * (RankRail과 동일 문법).
 */
export function RarityLadder({ className }: { className?: string }) {
  const top = CARD_FINISHES.length - 1;
  return (
    <section aria-label="Rarity ladder" className={className}>
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h2 className="font-display text-2xl font-bold text-ink">Rarity ladder</h2>
        <span className="shrink-0 font-mono text-[11px] text-ink-mute">low → high</span>
      </div>
      <div
        className={cn(
          // px-8 — 첫 칸/마지막 칸이 24px 엣지 마스크 구간 밖에서 시작·끝난다(improvements §M-19)
          "flex snap-x snap-mandatory items-end gap-3 overflow-x-auto px-8 pb-1 pt-2 scroll-pl-8",
          "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]",
        )}
      >
        {CARD_FINISHES.map((meta, index) => (
          <div
            key={meta.finish}
            className="flex w-[112px] shrink-0 snap-start flex-col gap-2 lg:w-[128px]"
            // 계단 오프셋 — 상위 등급일수록 6px씩 상승
            style={{ marginBottom: index * 6, marginTop: (top - index) * 6 }}
          >
            <FinishArt meta={meta} />
            {/* 칩 → 타이포 라벨: 이름 13/700 + 레어도색 + 이중부호 아이콘(R-26: ● open / ◆ serial / ★ 1-of-1) */}
            <p
              className={cn(
                "line-clamp-2 px-0.5 text-[13px] font-bold leading-tight",
                meta.accentText,
              )}
            >
              <span aria-hidden className="text-[9px]">
                {meta.supplyCap === 1 ? "★" : meta.supplyCap != null ? "◆" : "●"}
              </span>{" "}
              {meta.label}
            </p>
            <p className="px-0.5 font-mono text-[11px] font-medium tabular-nums text-ink-dim">
              {meta.supplyCap != null ? `/${meta.supplyCap}` : "Open edition"}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
